import * as React from "react";
import { Alert, Box, Button, Chip, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation } from "@apollo/client";
import { UPDATE_REFERRAL_SETTLEMENT_BY_REFERRER } from "../../graphql/mutations/care";
import { isOpenStatus, LeadStatus, OPEN_STATUSES } from "../utils/statuses";

type SettlementInlineProps = {
  lead: {
    id: string | number;
    status: string;
    settlementTotalByReferrer?: number | null;
  };
  onUpdated?: () => void;
  editable?: boolean;
};

export default function SettlementInline({
  lead,
  onUpdated,
  editable = true, // 👈 NEW: set to false in concierge to make it view-only
}: SettlementInlineProps) {
  /* ------------------------ formatting helpers ------------------------ */
  const fmtUSD = (v: number | string | null | undefined) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "Not set";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `$${n.toFixed(2)}`;
    }
  };
  const parseCurrency = (s: string) => {
    const cleaned = s.replace(/[^\d.,-]/g, "").replace(",", ".");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  };

  /* ------------------------------ state ------------------------------ */
  const initialAmount =
    lead?.settlementTotalByReferrer != null
      ? String(lead.settlementTotalByReferrer)
      : "";

  // If not editable, force view mode
  const [editing, setEditing] = React.useState<boolean>(
    editable ? !initialAmount : false
  );
  const [amount, setAmount] = React.useState<string>(initialAmount);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  // Always safe to declare; we simply won't use it in view-only mode
  const [mutate] = useMutation(UPDATE_REFERRAL_SETTLEMENT_BY_REFERRER, {
    refetchQueries: ["ReferrerByToken", "MyReferrerProfile"],
    awaitRefetchQueries: true,
  });

  /* ----------------------------- actions ----------------------------- */
  const onSave = async () => {
    if (!editable) return; // guard: shouldn't be called in view mode
    setError(null);
    setInfo(null);
    const numeric = parseCurrency(amount);
    if (!amount || Number.isNaN(numeric) || numeric <= 0) {
      setError("Enter a valid amount greater than 0.");
      return;
    }
    try {
      setSaving(true);
      await mutate({
        variables: {
          input: {
            leadId: String(lead.id),
            settlementTotalByReferrer: numeric,
          },
        },
        optimisticResponse: {
          updateReferralSettlementByReferrer: {
            __typename: "ReferralLead",
            id: String(lead.id),
            settlementTotalByReferrer: numeric,
            updatedAt: new Date().toISOString(),
          },
        },
      });
      setInfo(
        isOpenStatus(lead.status)
          ? "Saved. We’ll follow up with the provider while this is open."
          : "Saved."
      );
      setEditing(false);
      onUpdated?.();
    } catch (e: any) {
      setError(e?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    setAmount(initialAmount);
    setError(null);
    setInfo(null);
    setEditing(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    }
  };

  /* ------------------------------ display ---------------------------- */
  // In view-only mode, show the stored value only
  const viewValue =
    lead?.settlementTotalByReferrer != null
      ? fmtUSD(lead.settlementTotalByReferrer)
      : "Not set";

  // In edit-enabled mode, reflect live input when editing
  const editingValue =
    amount && !Number.isNaN(parseCurrency(amount))
      ? fmtUSD(parseCurrency(amount))
      : lead?.settlementTotalByReferrer != null
        ? fmtUSD(lead.settlementTotalByReferrer)
        : "Not set";

  const chipLabel = editable ? editingValue : viewValue;
  const hasValue = chipLabel !== "Not set";

  return (
    <Box>
      {/* Title row + value chip; controls appear only when editable */}
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", minWidth: 88 }}
        >
          Settlement by referrer
        </Typography>

        <Chip
          size="small"
          color={hasValue ? "success" : "default"}
          variant={hasValue ? "filled" : "outlined"}
          label={chipLabel}
        />

        {/* Right side: edit controls (hidden in concierge/view-only) */}
        {editable && (
          <Box sx={{ ml: "auto" }}>
            {!editing ? (
              <Button
                size="small"
                variant="text"
                onClick={() => setEditing(true)}
              >
                Update
              </Button>
            ) : (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={0.75}
                alignItems="center"
              >
                <TextField
                  size="small"
                  type="text"
                  label="Amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onKeyDown={onKeyDown}
                  sx={{ width: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                    inputMode: "decimal",
                  }}
                />
                <Stack direction="row" gap={0.5}>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    onClick={onSave}
                    disabled={saving}
                    startIcon={<CheckIcon />}
                  >
                    {saving ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onCancel}
                    startIcon={<CloseIcon />}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </Stack>

      {/* Messages (only relevant when editing) */}
      {editable && info && (
        <Alert severity="success" sx={{ mt: 1 }}>
          {info}
        </Alert>
      )}
      {editable && error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
